
import React, {useContext, useEffect, useState} from 'react';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {PAContext} from "../../../Services/PAContext";
import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Tab, Tooltip
} from "@mui/material";
import Badge from "@mui/material/Badge";
import {formatName, stringAvatar, stringToColor, capitalizeFirstLetter} from "../../Helpers/TextStyling";
import Typography from "@mui/material/Typography";
import {useNavigate} from "react-router-dom";
import './documents.css';
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import Box from "@mui/material/Box";
import DocumentCard from "./DocumentCard";
import { deleteAccountDocument } from "./BrowserDatabaseFunctions";
import {Stack} from "@mui/system";
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";
import {Info, UserCheck, FileLock, Plus} from "lucide-react";
import IconButton from "@mui/material/IconButton";
import DocumentEditCard from "./DocumentEditCard";




const EmptyState = ({ message, icon }) => (
    <Box sx={{ textAlign: 'center', py: 8, opacity: 0.4 }}>
        <i className={icon} style={{ fontSize: '40px', color: '#151269' }} />
        <Typography variant="body2" sx={{ fontWeight: 700, mt: 1, color: '#151269' }}>
            {message}
        </Typography>
    </Box>
);



function DocumentsList(){

    const navigate = useNavigate();

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, setAccountDocumentArray, sendSocketMessage} = useContext(PAContext);
    const { Account, SampleAttendees, authenticated  } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);
    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const [accountDocuments, setAccountDocuments] = useState(Account.documents || []);
    const [selectedAttendee, setSelectedAttendee] = useState(Account.id);
    const [filteredDocuments, setFilteredDocuments] = useState([]);

    const [anchorDocumentsListMenu, setAnchorDocumentsListMenu] = useState(null);
    const handleDocumentsListMenuOpenMenu = (event) => {
        setAnchorDocumentsListMenu(event.currentTarget);
    };
    const handleDocumentsListMenuClick = (event) => {
        const target = event.currentTarget;
        const dataId = target.getAttribute('data-id');

        handleDocumentsListMenuClose();

        if (dataId === "new-entry"){
            navigate('/Planner/Documents/AddNew');
        }
    };
    const handleDocumentsListMenuClose = () => {
        setAnchorDocumentsListMenu(null);
    };

    const handleChipClick = (_attendee_id) => {
        console.log("Show documents for : "+_attendee_id);
        setSelectedAttendee(_attendee_id);
    }

    const [sortedAttendees, setSortedAttendees] = useState([]);



    // --- EFFECT 1: Recalculate and sort attendees whenever documents change ---
    useEffect(() => {
        console.log("Recalculating attendees list due to PA_GlobalObject.Planning.documents change.");

        const attendeesWithDocumentCounts = PA_GlobalObject.Planning.attendees.map((attendee) => {
            const documentCount = PA_GlobalObject.Planning.documents.reduce((count, item) =>
                // The main filter logic is here
                count + ((item.attendee_id === attendee.id && (!item.private || item.attendee_id === Account.id)) ? 1 : 0), 0
            );
            return { ...attendee, documentCount };
        });

        // Split and sort the lists
        const attendeesWithDocuments = attendeesWithDocumentCounts.filter(attendee => attendee.documentCount > 0);
        const attendeesWithoutDocuments = attendeesWithDocumentCounts.filter(attendee => attendee.documentCount === 0);

        attendeesWithDocuments.sort((a, b) => b.documentCount - a.documentCount);
        attendeesWithoutDocuments.sort((a, b) => {
            const nameA = a.nickname || "";
            const nameB = b.nickname || "";
            return nameA.localeCompare(nameB);
        });

        // Update the state with the new, sorted list
        const newSortedAttendees = [...attendeesWithDocuments, ...attendeesWithoutDocuments];
        setSortedAttendees(newSortedAttendees);

    }, [PA_GlobalObject.Planning.documents, PA_GlobalObject.Planning.attendees, Account.id]); // Dependencies are crucial!

    // --- EFFECT 2: Filter documents for the currently selected attendee ---
    useEffect(() => {
        // This hook now runs on mount and whenever selectedAttendee or documents change.
        console.log("PA_GlobalObject.Planning.documents : " , PA_GlobalObject.Planning.documents);
        console.log("filterDocuments for Event for : " + selectedAttendee);

        // IMPORTANT: The filtering logic needs the latest documents and the selectedAttendee
        const filteredItems = PA_GlobalObject.Planning.documents.filter(document =>
            document.attendee_id === selectedAttendee && (!document.private || document.attendee_id === Account.id)
        );

        console.log("setting the filterDocuments to : ", filteredItems);
        setFilteredDocuments(filteredItems);

    }, [PA_GlobalObject.Planning.documents, selectedAttendee, Account.id, setFilteredDocuments]); // Dependencies!

    // --- EFFECT 3: Update Account documents state (Your original code) ---
    useEffect(() => {
        console.log("accountDocuments : ", Account.documents);
      setAccountDocuments(Account.documents);
    }, [Account.documents]);


    const [tabValue, setTabValue] = useState("event");
    const handleTabChange  = (event, newValue) => {
        setTabValue(newValue);
    };


    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    function onEditDocument(doc) {
        console.log("onEditDocument", doc);
        setSelectedDoc(doc); // Store the doc to be edited
        setIsEditOpen(true);  // Open the Dialog
    }


    const [documentToDelete, setDocumentToDelete] = useState(null);  // Holds the activity to delete for confirmation dialog
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);  // Controls the confirmation dialog visibility
    const openDeleteDialog = (doc) => {
        setDocumentToDelete(doc);  // Set the document to be deleted
        setDeleteDialogOpen(true);  // Open the confirmation dialog
    };
    const closeDeleteDialog = () => {
        setDocumentToDelete(null);  // Reset the document to be deleted
        setDeleteDialogOpen(false);  // Close the confirmation dialog
    };
    function handleDeleteDocument(document_id) {
        console.log("handleDeleteDocument:", document_id);
        if (!document_id) return;

        // 0) Locate the document and infer its source
        const eventDocs = PA_GlobalObject?.Planning?.documents ?? [];
        const accountDocs = Array.isArray(accountDocuments) ? accountDocuments : [];

        const foundInEvent = eventDocs.find((d) => d?.id === document_id);
        const foundInAccount = accountDocs.find((d) => d?.id === document_id);

        const accountOrEvent = foundInEvent ? "event" : foundInAccount ? "account" : null;

        if (!accountOrEvent) {
            console.warn("Document not found in either account or event collections:", document_id);
            return;
        }

        // 1) Optimistic UI update (remove from the correct in-memory state)
        if (accountOrEvent === "event") {
            set_PA_GlobalObject((prev) => {
                const prevPlanning = prev?.Planning || {};
                const existing = Array.isArray(prevPlanning.documents) ? prevPlanning.documents : [];
                const filtered = existing.filter((doc) => doc?.id !== document_id);
                if (filtered.length === existing.length) return prev; // no change
                return {
                    ...prev,
                    Planning: {
                        ...prevPlanning,
                        documents: filtered,
                    },
                };
            });

            try {
                if (PA_GlobalObject.Planning.status !== 10) {
                    const _sendString = {
                        action: "E_deleteEventDocument",
                        event_id: PA_GlobalObject.Planning.id,
                        document_id: document_id,
                    };
                    sendSocketMessage(_sendString);
                }
            } catch (err) {
                console.error("Server delete notify failed:", err);
                // TODO: optionally rollback optimistic update
            }
        } else {
            // Account docs collection
            const updatedDocumentList = Account.documents.filter((_doc)=>_doc.id !== document_id);
            setAccountDocumentArray(updatedDocumentList);

            try {
                if (PA_GlobalObject.Planning.status !== 10) {
                    const _sendString = {
                        action: "deleteAccountDocument",
                        document_id: document_id,
                    };
                    sendSocketMessage(_sendString);
                }
            } catch (err) {
                console.error("Server delete notify failed:", err);
                // TODO: optionally rollback optimistic update
            }
        }

        closeDeleteDialog();
    }

    const [isDocumentsInfoDialogOpen, setDocumentsInfoDialogOpen] = useState(false);


    // --- EFFECT 4: Handle Initial Landing Logic ---
    useEffect(() => {
        // 1. Ensure the current user is selected as the active attendee
        // We use Account.id which is the logged-in user's unique ID
        setSelectedAttendee(Account.id);

        // 2. Determine document counts to set the initial tab
        // Event documents belong to the specific event context
        const eventDocs = PA_GlobalObject.Planning.documents.filter(doc =>
            doc.attendee_id === Account.id
        );

        // Account documents are the user's general documents
        const accountDocs = Account.documents || [];

        if (eventDocs.length > 0) {
            // Priority 1: If there are event docs, go to 'event'
            setTabValue("event");
        } else if (accountDocs.length > 0) {
            // Priority 2: No event docs but there are account docs, go to 'account'
            setTabValue("account");
        } else {
            // Priority 3: Neither exist, default to 'event'
            setTabValue("event");
        }

        // NOTE: Empty dependency array [] or [Account.id] so it only triggers
        // when you "switch" to this view / component mounts.
    }, [Account.id]);

    const isLiveAndAuth = PA_GlobalObject.Planning.status !== 10;

    // Determine the message to show the user
    const getDisabledReason = () => {
        if (!authenticated) return "Please log in to add entries";
        if (PA_GlobalObject.Planning.status === 10) return "Event must be booked and live to add entries";
        return "";
    };


    return (
        <div className="documents-list-container">

            {/* show the header */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Title Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)'
                        }}>
                            <FileLock size={18} color="white" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                Documents
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <UserCheck size={10} color="#8b5cf6" />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                    Secure Storage Vault
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Controls Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Info Icon */}
                        <Tooltip title="Info">
                            <IconButton
                                onClick={() => setDocumentsInfoDialogOpen(!isDocumentsInfoDialogOpen)}
                                sx={{ color: '#64748b' }}
                            >
                                <Info size={20} />
                            </IconButton>
                        </Tooltip>

                        {/* Direct Add Button */}
                        <Tooltip title="Add New Document">
                            <IconButton
                                data-id="new-entry"
                                onClick={handleDocumentsListMenuClick}
                                sx={{
                                    ml: 0.5,
                                    bgcolor: '#1e293b',
                                    color: 'white',
                                    borderRadius: '12px',
                                    width: '38px',
                                    height: '38px',
                                    boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: '#334155',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 6px 16px rgba(30, 41, 59, 0.3)',
                                    },
                                    '&:active': { transform: 'translateY(0px)' }
                                }}
                            >
                                <Plus size={20} strokeWidth={2.5} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {/* Attendee List */}
            <div className="documents-list-attendee-list-container" style={{
                display: 'flex',
                overflowX: 'auto',
                msOverflowStyle: 'none',
                alignItems: 'flex-start'
            }}>
                {sortedAttendees.map((attendee) => {
                    // 1. Logic to find the real avatar data
                    const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === attendee.id);
                    const isSelected = selectedAttendee === attendee.id;

                    const fullName = attendee.nickname?.trim()
                        ? `${formatName(attendee.nickname)}`
                        : `${formatName(attendee.fname)} ${formatName(attendee.lname)}`;

                    return (
                        <Box
                            key={attendee.id}
                            onClick={() => handleChipClick(attendee.id)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                minWidth: '60px',
                                transition: 'transform 0.2s ease',
                                '&:hover': {transform: 'translateY(-2px)'}
                            }}
                        >
                            {/* 2. The Notification Badge for Document Counts */}
                            <Badge
                                badgeContent={attendee.documentCount > 0 ? attendee.documentCount : null}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: '#E0F2F1', // Soft Mint/Teal
                                        color: '#00695C',           // Dark Teal text
                                        fontSize: '10px',
                                        fontWeight: 900,
                                        border: '2px solid #fff',
                                        top: 5,
                                        right: 5
                                    },
                                }}
                            >
                                {/* 3. The Avatar */}
                                <Box sx={{
                                    p: '3px',
                                    borderRadius: '50%',
                                    border: isSelected ? '2px solid #151269' : '2px solid transparent',
                                    transition: 'all 0.3s ease'
                                }}>
                                    {
                                        !attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                            <Avatar
                                                {...stringAvatar(fullName)}
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                    border: `1px solid ${stringToColor(fullName)}`
                                                }}
                                            />
                                        ) : (
                                            <Avatar
                                                src={attendeeAvatar.avatar.preview}
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                    border: `1px solid ${stringToColor(fullName)}`
                                                }}
                                            />
                                        )
                                    }
                                </Box>
                            </Badge>

                            {/* 4. Name Label */}
                            <Typography sx={{
                                fontSize: '11px',
                                fontWeight: isSelected ? 800 : 500,
                                mt: 1,
                                color: isSelected ? '#151269' : '#666',
                                textAlign: 'center',
                                maxWidth: '70px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {capitalizeFirstLetter(attendee.nickname || attendee.fname)}
                            </Typography>
                        </Box>
                    );
                })}
            </div>

            {/* Documents List */}
            <div className="documents-list-items-container" style={{backgroundColor: '#f8f9fa'}}>
                {selectedAttendee === Account.id ? (
                    <TabContext value={tabValue}>
                        {/* 🛠 CUSTOM SEGMENTED PILL TABS */}
                        <Box sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            pt: 1,
                            pb: 1,
                            px: 2,
                            // Change to a solid color to hide documents perfectly
                            backgroundColor: '#f8f9fa',
                            // Add a slight border or shadow to give it depth as items go under it
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            // Optional: remove backdropFilter if you go solid, it's no longer needed
                            backdropFilter: 'none'
                        }}>
                            <TabList
                                onChange={handleTabChange}
                                centered
                                variant="fullWidth"
                                sx={{
                                    minHeight: '40px',
                                    backgroundColor: 'rgba(21, 18, 105, 0.05)',
                                    borderRadius: '12px',
                                    p: '4px',
                                    '& .MuiTabs-indicator': {
                                        height: '100%',
                                        borderRadius: '10px',
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    },
                                }}
                            >
                                <Tab
                                    value="event"
                                    disableRipple
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography
                                                sx={{fontSize: '12px', fontWeight: 800, zIndex: 1}}>Event</Typography>
                                            {filteredDocuments.length > 0 && (
                                                <Box sx={{
                                                    bgcolor: tabValue === 'event' ? '#151269' : 'rgba(21, 18, 105, 0.1)',
                                                    color: tabValue === 'event' ? '#fff' : '#151269',
                                                    px: 0.8,
                                                    borderRadius: '6px',
                                                    fontSize: '10px',
                                                    fontWeight: 900,
                                                    zIndex: 1
                                                }}>
                                                    {filteredDocuments.length}
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    sx={{
                                        minHeight: '32px',
                                        textTransform: 'none',
                                        color: '#888',
                                        '&.Mui-selected': {color: '#151269'}
                                    }}
                                />
                                <Tab
                                    value="account"
                                    disableRipple
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography
                                                sx={{fontSize: '12px', fontWeight: 800, zIndex: 1}}>Account</Typography>
                                            {accountDocuments?.length > 0 && (
                                                <Box sx={{
                                                    bgcolor: tabValue === 'account' ? '#151269' : 'rgba(21, 18, 105, 0.1)',
                                                    color: tabValue === 'account' ? '#fff' : '#151269',
                                                    px: 0.8,
                                                    borderRadius: '6px',
                                                    fontSize: '10px',
                                                    fontWeight: 900,
                                                    zIndex: 1
                                                }}>
                                                    {accountDocuments.length}
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    sx={{
                                        minHeight: '32px',
                                        textTransform: 'none',
                                        color: '#888',
                                        '&.Mui-selected': {color: '#151269'}
                                    }}
                                />
                            </TabList>
                        </Box>

                        {/* CONTENT AREA */}
                        <TabPanel value="event" sx={{p: 2, pt: 1}}>
                            {filteredDocuments && filteredDocuments.length > 0 ? (
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                    {filteredDocuments.map((doc) => (
                                        <DocumentCard
                                            key={doc.id}
                                            attendeeDocument={doc}
                                            onEditDocument={onEditDocument}
                                            openDeleteDialog={openDeleteDialog}
                                        />
                                    ))}
                                </Box>
                            ) : <EmptyState message="No Event Documents" icon="icon-folder-user"/>}
                        </TabPanel>

                        <TabPanel value="account" sx={{p: 2, pt: 1}}>
                            {accountDocuments && accountDocuments.length > 0 ? (
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                    {accountDocuments.map((doc) => (
                                        <DocumentCard
                                            key={doc.id}
                                            attendeeDocument={doc}
                                            onEditDocument={onEditDocument}
                                            openDeleteDialog={openDeleteDialog}
                                        />
                                    ))}
                                </Box>
                            ) : <EmptyState message="Your Account Vault is empty" icon="icon-shield-check"/>}
                        </TabPanel>
                    </TabContext>
                ) : (
                    /* OTHERS' VIEW */
                    <Box sx={{p: 2}}>
                        <Typography variant="overline"
                                    sx={{fontWeight: 800, color: '#151269', opacity: 0.5, mb: 2, display: 'block'}}>
                            Shared Documents
                        </Typography>
                        {filteredDocuments && filteredDocuments.length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                {filteredDocuments.map((doc) => (
                                    <DocumentCard
                                        key={doc.id}
                                        attendeeDocument={doc}
                                        isUploading={doc.data === 'uploading...'}
                                    />
                                ))}
                            </Box>
                        ) : <EmptyState message="No shared documents found" icon="icon-eye-cross"/>}
                    </Box>
                )}
            </div>

            {/* Edit Dialog */}
            <DocumentEditCard
                open={isEditOpen}
                editingDoc={selectedDoc}
                onClose={() => {
                    setIsEditOpen(false);
                    setSelectedDoc(null); // Clean up on close
                }}
            />

            {/* Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={closeDeleteDialog}
                // Added maxWidth and fullWidth for better mobile and desktop display
                maxWidth="sm"
                // Optional: Add a class for custom styling if needed
                PaperProps={{sx: {borderRadius: "20px"}}}
            >
                {/* Use a Box for the content to apply padding, alignment, and styling */}
                <Box sx={{p: 4, textAlign: 'center'}}>
                    {/* Icon for the header */}
                    <Box
                        sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgb(250,227,225)',
                            p: 2,
                            borderRadius: '50%',
                            mb: 2,
                        }}
                    >
                        <i className="icon-warning" style={{color: 'rgb(211, 47, 47)', fontSize: '40px'}}/>
                    </Box>

                    {/* Title */}
                    <DialogTitle
                        id="delete-modal-title"
                        variant="h4"
                        component="h2"
                        fontWeight="bold"
                        sx={{color: 'text.primary', m: 0, p: 0}}
                    >
                        A Moment of Caution
                    </DialogTitle>

                    {/* Body Text */}
                    <DialogContent sx={{p: 0, "&:first-of-type": {pt: 0}}}>
                        <Typography
                            variant="body1"
                            sx={{mt: 2, color: "text.secondary", lineHeight: 1.6}}
                        >
                            <Box
                                component="span"
                                sx={{fontWeight: "bold", color: "error.dark"}}
                            >
                                Whoa!
                            </Box>{" "}
                            Deleting this document will permanently remove it from your account, and
                            <Box
                                component="span"
                                sx={{fontWeight: "bold", color: "error.dark"}}
                            >
                                {" "}
                                this action cannot be undone
                            </Box>
                            .
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{mt: 2, color: "text.secondary", lineHeight: 1.6}}
                        >
                            Once deleted, the document and its encrypted data will be gone forever.
                            If you think you might need it later, consider downloading a copy first.
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{mt: 3, fontWeight: "bold", color: "text.primary"}}
                        >
                            Are you sure you want to permanently delete the document{" "}
                            <Box component="span" sx={{color: "error.dark"}}>
                                “{documentToDelete?.name}”
                            </Box>
                            ?
                        </Typography>
                    </DialogContent>


                    {/* Buttons */}
                    <DialogActions sx={{p: 0, mt: 4, justifyContent: 'center'}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="center">
                            <Button
                                onClick={closeDeleteDialog}
                                variant="contained"
                                sx={{
                                    color: 'text.primary',
                                    bgcolor: 'grey.300',
                                    '&:hover': {
                                        bgcolor: 'grey.400',
                                    },
                                    borderRadius: '10px',
                                    boxShadow: 3,
                                    flexGrow: 1,
                                }}
                            >
                                No, Keep It
                            </Button>
                            <Button
                                onClick={() => handleDeleteDocument(documentToDelete.id)}
                                variant="contained"
                                sx={{
                                    bgcolor: 'error.dark',
                                    '&:hover': {
                                        bgcolor: 'error.main',
                                    },
                                    borderRadius: '10px',
                                    boxShadow: 3,
                                    flexGrow: 1,
                                }}
                            >
                                Yes, Delete Document
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>
            </Dialog>

        </div>
    )
}

export default DocumentsList;